// Shared types for the client
export enum UserRole {
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',
  AUCTIONEER = 'AUCTIONEER',
  STAFF = 'STAFF',
  BIDDER = 'BIDDER'
}

export enum AuctionType {
  SILENT = 'SILENT',
  LIVE = 'LIVE',
  DUTCH = 'DUTCH'
}

export enum SSLCertificateStatus {
  PENDING = 'PENDING',
  ISSUED = 'ISSUED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  RENEWING = 'RENEWING'
}

export type SSLCertificate = {
  status: SSLCertificateStatus;
  issuedAt?: Date;
  expiresAt?: Date;
  issuer?: string; // e.g., "Let's Encrypt"
  serialNumber?: string;
  lastRenewalAttempt?: Date;
  renewalError?: string;
};

export type EmailDnsRecord = {
  type: 'CNAME' | 'TXT' | 'MX';
  name: string;
  value: string;
  verified?: boolean;
  purpose: 'dkim' | 'spf' | 'dmarc' | 'return_path';
  priority?: number; // MX record priority (Resend return path records)
  ttl?: string; // Suggested TTL, e.g. "Auto"
};

/** Outbound email provider used for domain authentication */
export type EmailProviderName = 'sendgrid' | 'resend';

export type AuctionDefaults = {
  defaultStartTime: string;
  defaultEndTime: string;
  defaultDurationDays: number;
  defaultTimezone: string;
  defaultCurrency: string;
  defaultBidIncrement: number;
  defaultAuctionType: string;
  defaultVisibility: string;
  defaultAllowSnipingExtension: boolean;
  defaultSnipingWindowSeconds: number;
  defaultExtensionSeconds: number;
  defaultMaxExtensions: number;
  defaultFraudChecksLevel: string;
  defaultTaxRatePercent: number;
  defaultPrimaryColor: string;
  defaultWebsite?: string;
};

/**
 * Analytics and advertising pixels an auctioneer runs on their own public
 * pages. Measurement ids only - the loader script is built from the id, so no
 * pasted markup ever reaches a bidder's browser.
 */
export type TrackingSettings = {
  googleAnalyticsId?: string; // GA4 "G-XXXXXXX" or legacy "UA-XXXXX-Y"
  googleTagManagerId?: string; // "GTM-XXXXXXX"
  googleAdsConversionId?: string; // "AW-XXXXXXXXX"
  metaPixelId?: string; // Facebook/Instagram pixel id (digits)
  tiktokPixelId?: string;
  linkedInPartnerId?: string;
  pinterestTagId?: string;
};

export type MarketingImageQuality = 'low' | 'medium' | 'high';

/**
 * Brand direction applied to every marketing image the account generates, so a
 * set of promotional images looks like it came from one organisation.
 */
export type MarketingSettings = {
  brandTone?: string;
  primaryColor?: string;
  secondaryColor?: string;
  includeLogo?: boolean;
  includeHeadline?: boolean;
  callToAction?: string;
  styleNotes?: string;
  avoidNotes?: string;
  defaultChannels?: string[];
  defaultQuality?: MarketingImageQuality;
};

/** An output size on offer, one per place an auctioneer actually posts. */
export type MarketingChannel = {
  id: string;
  label: string;
  description: string;
  size: string;
  width: number;
  height: number;
  framing: string;
};

/** One AI-generated promotional image, kept against the item it promotes. */
/** A promotional badge drawn onto a finished image. */
export type MarketingBadge = {
  style: 'urgency' | 'price' | 'custom';
  kicker?: string;
  text: string;
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
};

export type MarketingAsset = {
  id: string;
  accountId: string;
  auctionId: string;
  itemId: string;
  channel: string;
  imageUrl: string;
  contentType: string;
  width: number;
  height: number;
  bytes: number;
  userPrompt?: string;
  prompt: string;
  model: string;
  quality: MarketingImageQuality;
  origin: 'generated' | 'edited';
  parentAssetId?: string;
  /** Where the image has got to. Absent on rows written before queueing existed. */
  status?: 'pending' | 'ready' | 'failed';
  failureReason?: string;
  badges?: MarketingBadge[];
  /** The item photograph this image was built from, when generated per photo. */
  sourceImageUrl?: string;
  sourceImageIndex?: number;
  usedItemImages: number;
  usedLogo: boolean;
  createdBy: string;
  createdAt: string;
};

/** A library asset, carrying the titles the library lists it under. */
export type MarketingAssetWithContext = MarketingAsset & {
  itemTitle?: string | null;
  /** Shown on the card, since the library no longer groups by item. */
  itemSponsorName?: string | null;
  auctionTitle?: string | null;
};

export type Account = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  logoImageUrl?: string; // Organization logo image URL
  organizationPhone?: string; // Organization contact phone number
  organizationEmail?: string; // Organization contact email address
  customDomain?: string; // Custom domain name (e.g., "myauction.com")
  domainVerified?: boolean; // Whether the custom domain is verified via CNAME
  cnameVerified?: boolean; // Whether the CNAME record is correctly configured
  domainVerificationToken?: string; // Token for domain ownership verification (legacy)
  sslCertificate?: SSLCertificate; // SSL certificate information
  // Email domain authentication
  domainEmailAuthenticated?: boolean; // Whether the domain is authenticated for email sending
  sendgridDomainId?: string; // Legacy SendGrid domain authentication ID (superseded by emailDomainId)
  emailDomainId?: string; // Provider domain authentication ID (SendGrid or Resend)
  emailDomainProvider?: EmailProviderName; // Provider the domain was authenticated with
  emailSetupResetAt?: string; // When email authentication was last reset (e.g. on a provider switch)
  emailAuthenticationStatus?: 'pending' | 'verified' | 'failed'; // Email authentication status
  emailDnsRecords?: EmailDnsRecord[]; // Required DNS records for email authentication
  emailDomain?: string; // Custom email domain (can be different from customDomain)
  emailFromAddress?: string; // Full email address to send emails from (e.g., "empowermepink@kiaracosmetics.co.nz")
  emailFromName?: string; // Display name for outbound emails (e.g., "Kiara Cosmetics Team")
  // Verification results storage
  emailVerificationResults?: {
    domainCheck?: { status: 'checking' | 'success' | 'failed', message: string } | null;
    sslCheck?: { status: 'checking' | 'success' | 'failed', message: string } | null;
    returnPathCheck?: { status: 'checking' | 'success' | 'failed', message: string } | null;
    dkim1Check?: { status: 'checking' | 'success' | 'failed', message: string } | null;
    dkim2Check?: { status: 'checking' | 'success' | 'failed', message: string } | null;
    spfCheck?: { status: 'checking' | 'success' | 'failed', message: string } | null;
    dmarcCheck?: { status: 'checking' | 'success' | 'failed', message: string } | null;
  };
  emailVerificationLastChecked?: string; // Last time verification was performed (ISO string)
  stripeCustomerId?: string; // Stripe customer ID
  // Stripe Connect fields (optional - defaults to platform account)
  stripeConnectAccountId?: string; // Stripe Connect account ID
  stripeAccountStatus?: 'pending' | 'active' | 'restricted' | 'rejected';
  stripeOnboardingCompleted?: boolean;
  stripeChargesEnabled?: boolean;
  stripePayoutsEnabled?: boolean;
  useOwnStripeAccount?: boolean; // Whether to use their own account vs platform default
  platformFeePercentage?: number; // Platform fee when using their own account (e.g., 2.5%)
  auctionDefaults?: AuctionDefaults; // Default auction settings
  tracking?: TrackingSettings | null; // Marketing/analytics pixels for the account's public pages
  marketingSettings?: MarketingSettings | null; // Brand direction for AI-generated marketing images
  defaultBidIncrement?: number; // For API responses that include this field
  entryVideoUrl?: string; // Organization entry video for custom domain display
  createdAt: Date;
};

export type TenantInfo = {
  name: string;
  logoImageUrl?: string;
  entryVideoUrl?: string;
  customDomain: string;
  isCustomDomain: boolean;
};

export type User = {
  id: string;
  accountId: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  phone?: string;
  profileImageUrl?: string;
  profileImagePosition?: string;
  role: UserRole;
  isActive: boolean;
  onboardingCompleted?: boolean;
  invitedAt?: Date;
  activatedAt?: Date;
  lastLoginAt?: Date;
  lastRealLoginAt?: Date; // Last login excluding impersonation
  createdAt: Date;
  accountName?: string; // Account name (only available for platform admin views)
  blockedAt?: Date | null; // Set when a platform admin has blocked this person (platform admin views)
};

/** How a person on an auctioneer's bidder list came to be connected to them. */
export type BidderSource = 'BID' | 'SIGNED_UP' | 'VISITED' | 'INVITED';

/**
 * Somebody in an auctioneer's audience.
 *
 * Not a User: these people hold accounts of their own and are not members of
 * the auctioneer's account, which is precisely why they never appeared in the
 * Users list. `sources` says how each one is connected, because "bid on my
 * auction" and "looked but never bid" want different messages.
 */
export type AccountBidder = {
  id: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  profileImageUrl?: string;
  role?: string;
  createdAt?: string;
  lastLoginAt?: string;
  sources: BidderSource[];
  bidCount: number;
  auctionsBidOn: number;
  highestBid: number;
  firstBidAt?: string;
  lastBidAt?: string;
  itemsWon: number;
  amountWon: number;
  lastActivityAt?: string;
  signupAuctionId?: string;
  signupDomain?: string;
};

/** An account's bidder list, with the currency its figures are in. */
export type AccountBidderDirectory = {
  currency: string;
  bidders: AccountBidder[];
};

export type ImpersonationInfo = {
  isImpersonating: boolean;
  originalAdminId: string;
  impersonatedUserId: string;
  impersonatedBy: string;
  expiresAt: Date;
};

export type AuthUser = {
  user: User;
  account: Account;
  impersonation?: ImpersonationInfo;
  /** Whether the session is on Stripe test keys. Set by the server. */
  testModeEnabled?: boolean;
};

export enum AuctionStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  LIVE = 'LIVE',
  PAUSED = 'PAUSED',
  ENDED = 'ENDED',
  CANCELLED = 'CANCELLED'
}

export enum AuctionVisibility {
  PUBLIC = 'PUBLIC',
  UNLISTED = 'UNLISTED',
  INVITE_ONLY = 'INVITE_ONLY'
}

export enum FraudChecksLevel {
  NONE = 'NONE',
  BASIC = 'BASIC',
  STRICT = 'STRICT'
}

export type Auction = {
  id: string;
  accountId: string;
  ownerId: string;
  title: string;
  description?: string;
  auctionDetails?: string;
  coverImageUrl?: string; // Keep for backward compatibility
  bannerImageUrl?: string; // Banner image for auction display
  logoImageUrl?: string; // Logo image for auction branding
  entryVideoUrl?: string; // Entry video for auction display
  url: string;
  previousUrls?: string[]; // Slugs this auction used before; old links still resolve to it
  password?: string;
  requiresPassword?: boolean;
  startsAt: Date;
  endsAt: Date;
  timezone: string;
  status: AuctionStatus;
  visibility: AuctionVisibility;
  tags: string[];
  privateInviteCode?: string;
  bidIncrement?: number | null;
  /** How many items a page of the auction shows. Defaults to DEFAULT_ITEMS_PER_PAGE. */
  itemsPerPage?: number;
  currency: string;
  allowSnipingExtension: boolean;
  snipingWindowSeconds: number;
  extensionSeconds: number;
  maxExtensions: number;
  extensionCount?: number;
  maxItemsPerAuction?: number;
  taxRatePercent: number;
  shippingPolicy?: string;
  antiSnipeMinBidCount: number;
  fraudChecksLevel: FraudChecksLevel;
  auctionType: AuctionType;
  onDemand: boolean; // Whether this auction starts on demand
  durationDays?: number; // Duration in days for on-demand auctions
  primaryColor: string; // Primary color for auction theming
  website?: string; // Website link for the auction
  showAuctionName?: boolean; // Whether to show auction name in live auction view
  // Charity features
  charityBeneficiary?: string; // Name of the charity beneficiary
  goalAmount?: number; // Target fundraising goal amount
  charityFeeEnabled?: boolean; // Whether to show charity fee option during Buy Now
  beneficiaryLogoUrl?: string; // Logo image for the charity beneficiary
  beneficiaryEmail?: string; // Contact email for the charity beneficiary
  beneficiaryPhone?: string; // Contact phone for the charity beneficiary
  requirePaymentVerification?: boolean; // Whether payments require auctioneer verification before processing
  // Cached fundraising totals for performance
  cachedActualTotalRaised?: number; // Cached net total from completed transactions after fees (in cents)
  cachedEstimatedTotal?: number; // Cached net total including current top bids after fees (in cents)
  cachedGrossActualTotalRaised?: number; // Cached gross total from completed transactions before fees (in cents)
  cachedGrossEstimatedTotal?: number; // Cached gross total including current top bids before fees (in cents)
  cachedTotalCharityFees?: number; // Cached total charity fee compensations (in cents)
  cachedTotalsUpdatedAt?: Date; // When the cached totals were last updated
  // Bidder requirements
  bidderRequirements?: {
    requireFullName?: boolean; // Require first and last name
    requireAddress?: boolean; // Require address
    requirePhone?: boolean; // Require phone number
    requirePaymentMethod?: boolean; // Require credit card
  };
  createdAt: Date;
  // Draft preview flag
  isDraftPreview?: boolean; // True when showing draft auction in preview mode
};

export enum ItemStatus {
  ACTIVE = 'ACTIVE',
  SOLD = 'SOLD',
  PASSED = 'PASSED',
  CANCELLED = 'CANCELLED',
  LIVE_AUCTION = 'LIVE_AUCTION', // Item has reached closing time and is now in live auction mode
  RESERVE_PURCHASE_PENDING = 'RESERVE_PURCHASE_PENDING' // Item didn't meet reserve, waiting for leading bidder to purchase at reserve
}

export enum ItemType {
  ONLINE = 'ONLINE',
  LIVE = 'LIVE'
}

export type Item = {
  id: string;
  auctionId: string;
  title: string;
  description?: string;
  imageUrl?: string; // Keep for backward compatibility
  images?: string[]; // Array of image URLs
  mainImageIndex?: number; // Index of main image in images array
  startPrice: number;
  reservePrice?: number;
  buyNowPrice?: number; // Optional Buy Now price for instant purchase
  currentPrice: number;
  status: ItemStatus;
  itemType: ItemType; // Whether item is for live or online bidding
  sponsorName?: string; // Name of the product sponsor
  sponsorUrl?: string; // URL for the product sponsor
  sponsorLogoUrl?: string; // Sponsor's logo, composited into marketing imagery
  shippingLocation?: string; // Shipping location for the item
  bidIncrement?: number; // Custom bid increment for this item (overrides auction default)
  /** Pinned to the top of the auction, with a highlight. At most MAX_FEATURED_ITEMS per auction. */
  featured?: boolean;
  itemValue?: number; // Estimated monetary value of the item
  displayOrder?: number; // Order for displaying items in the auction
  itemStartDate?: Date | null; // Optional - overrides auction start date if set, null means no override
  itemEndDate?: Date | null; // Optional - overrides auction end date if set, null means no override
  extensionCount?: number; // Track how many times this item has been extended due to anti-sniping
  createdAt: Date;
  // Optional properties for extended views
  bids?: (Bid & { user: Pick<User, 'id' | 'displayName' | 'email' | 'profileImageUrl'> })[];
  bidCount?: number; // Efficient bid count for edit page (when bids array is not loaded)
  startingBid?: number; // Alias for startPrice
};

export type Bid = {
  id: string;
  itemId: string;
  userId: string;
  amount: number;
  isAutoBid?: boolean;
  maxBid?: number | null;
  isBuyNow?: boolean;
  isLiveAuctionWinner?: boolean; // Flag to indicate this is a live auction winner bid
  liveWinnerName?: string; // Name of the live auction winner
  displayName?: string; // Display name for bid history (either user displayName or liveWinnerName)
  email?: string; // Email for bid history (either user email or live winner email)
  createdAt: Date;
};

// Extended types with relations
export type AuctionWithItems = Auction & {
  items: Item[];
  account: Pick<Account, 'id' | 'name' | 'role' | 'logoImageUrl' | 'customDomain' | 'defaultBidIncrement' | 'organizationPhone' | 'organizationEmail' | 'email' | 'entryVideoUrl'>;
  owner?: Pick<User, 'id' | 'displayName' | 'role'>;
  isDraftPreview?: boolean; // Explicitly include for TypeScript
  counts?: {
    items: number;
    transactions: number;
    raffles: number;
    winners: number;
  };
  // 🚀 NEW: Preloaded tab data for instant tab switching
  tabData?: {
    items?: Item[];
    transactions?: Transaction[];
    raffles?: any[];
  };
  preloadedTransactions?: Transaction[]; // For backward compatibility and easy access
};

// Auction access response type (for password/invite protected auctions)
export type AuctionAccessResponse = Partial<Auction> & {
  id: string;
  title: string;
  description?: string;
  url: string;
  visibility: AuctionVisibility;
  requiresPassword?: boolean;
  requiresInviteCode?: boolean;
  status: AuctionStatus;
  startsAt: Date;
  endsAt: Date;
  owner?: Pick<User, 'id' | 'displayName' | 'role'>;
};

// User auction association type
export type UserAuction = {
  id: string;
  userId: string;
  auctionId: string;
  savedAt: Date;
  accessedAt: Date;
  hasPassword?: boolean;
  firstVisitAt?: Date;
  hasSeenSavePrompt?: boolean;
};

export type UserAuctionWithDetails = UserAuction & {
  auction: AuctionWithItems;
};

export type ItemWithBids = Item & {
  bids?: (Bid & { user: Pick<User, 'id' | 'displayName' | 'profileImageUrl' | 'email'> })[];
  auction: Pick<Auction, 'id' | 'title' | 'status' | 'startsAt' | 'endsAt' | 'ownerId' | 'charityBeneficiary' | 'goalAmount' | 'charityFeeEnabled' | 'bidIncrement' | 'onDemand' | 'timezone' | 'currency'> & {
    account?: Pick<Account, 'defaultBidIncrement'>;
  };
  startingBid?: number; // Alias for startPrice for backward compatibility
};

export type BidWithUser = Bid & {
  user: Pick<User, 'id' | 'displayName' | 'profileImageUrl' | 'email'>;
};

export type Donation = {
  id: string;
  amount: number; // Amount in cents
  currency: string;
  message?: string; // Optional message from donor (max 160 chars)
  createdAt: Date;
  donor: {
    displayName: string;
    profileImageUrl?: string | null;
  };
};

// Stripe-related types
export type PaymentMethod = {
  id: string;
  accountId: string;
  stripePaymentMethodId: string;
  type: 'card';
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
  isDefault: boolean;
  createdAt: Date;
};

export type Invoice = {
  id: string;
  accountId: string;
  stripeInvoiceId: string;
  itemId: string;
  auctionId: string;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  description?: string;
  paidAt?: Date;
  createdAt: Date;
  // Extended properties
  item?: Pick<Item, 'id' | 'title'>;
  auction?: Pick<Auction, 'id' | 'title'>;
};

export type Transaction = {
  id: string;
  accountId: string;
  itemId?: string; // Optional for donations
  auctionId: string;
  bidId?: string; // Optional for donations
  raffleId?: string; // Optional for raffle ticket purchases
  stripePaymentIntentId?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'canceled' | 'refunded' | 'voided' | 'pending_verification';
  paymentMethodId?: string;
  description?: string;
  type?: 'payment' | 'charity_fee_compensation' | 'donation' | 'manual_payment' | 'cash_payment' | 'external_payment' | 'item_purchase' | 'item_purchase_with_charity' | 'raffle_ticket_purchase'; // Type of transaction
  verificationRequired?: boolean;
  verifiedAt?: Date;
  verifiedBy?: string;
  message?: string; // Optional message from donor (max 160 chars)
  displayName?: string; // Display name for transaction history (either user displayName or live winner name)
  createdAt: Date;
  // Extended properties
  item?: Pick<Item, 'id' | 'title'>;
  auction?: Pick<Auction, 'id' | 'title'>;
  paymentMethod?: Pick<PaymentMethod, 'id' | 'card'>;
  // Platform admin additional fields
  userEmail?: string;
  userName?: string;
  /** Donations: the donor asked not to be named in the public donation list. */
  isAnonymous?: boolean;
  userFullName?: string;
  accountName?: string;
  auctionTitle?: string;
  auctioneerName?: string;
  auctioneerEmail?: string;
};

/** One try at charging a card for a transaction: the first charge and every retry. */
export type PaymentAttempt = {
  id: string;
  transactionId: string;
  auctionId: string;
  source: 'auction_close' | 'auctioneer_retry' | 'bidder_retry' | 'item_offer';
  outcome: 'succeeded' | 'failed' | 'requires_action' | 'pending' | 'canceled';
  amount: number; // cents
  currency: string;
  stripePaymentIntentId?: string;
  paymentMethodId?: string;
  card?: { brand?: string; last4?: string };
  failureCode?: string;
  declineCode?: string;
  failureMessage?: string;
  initiatedByUserId?: string;
  initiatedByName?: string;
  fromStripe?: boolean; // Read from Stripe, made before history was recorded
  notes?: string;
  createdAt: string;
};

// Raffle types
export enum RaffleStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ENDED = 'ENDED',
  CANCELLED = 'CANCELLED'
}

export type RaffleTicketPack = {
  id: string;
  tickets: number; // Number of tickets in this pack
  price: number; // Price for this pack
  description?: string; // Optional description like "Best Value!"
};

export type Raffle = {
  id: string;
  auctionId: string;
  title: string;
  description?: string;
  images?: string[]; // Array of image URLs (base64 or URLs)
  mainImageIndex?: number; // Index of main image in images array
  ticketPacks: RaffleTicketPack[];
  totalTickets: number; // Total number of tickets available
  soldTickets: number; // Number of tickets sold
  status: RaffleStatus;
  drawDate?: Date; // When the raffle will be drawn
  winnerTicketNumber?: number; // The winning ticket number
  winnerId?: string; // ID of the winner
  winnerName?: string; // Display name of the winner
  createdAt: Date;
};

export type RaffleWithItems = Raffle & {
  items?: RaffleItem[];
};

export type RaffleTicket = {
  id: string;
  raffleId: string;
  auctionId: string;
  userId: string;
  ticketNumber: number;
  packId: string; // Which pack this ticket was purchased from
  allocatedToItemId?: string; // Which raffle item this ticket is allocated to (optional - unallocated tickets can be distributed later)
  purchasedAt: Date;
};

export enum RaffleItemStatus {
  ACTIVE = 'ACTIVE',
  SOLD = 'SOLD',
  CANCELLED = 'CANCELLED'
}

export type RaffleItem = {
  id: string;
  raffleId: string;
  auctionId: string;
  title: string;
  description?: string;
  imageUrl?: string; // Keep for backward compatibility
  images?: string[]; // Array of image URLs
  mainImageIndex?: number; // Index of main image in images array
  estimatedValue?: number; // Estimated monetary value of the item
  sponsorName?: string; // Name of the product sponsor
  sponsorUrl?: string; // URL for the product sponsor
  sponsorLogoUrl?: string; // Sponsor's logo, composited into marketing imagery
  status: RaffleItemStatus;
  displayOrder?: number; // Order for displaying items in the raffle
  createdAt: Date;
};

// API Response types
export type ApiResponse<T = any> = {
  success: true;
  data: T;
} | {
  success: false;
  error: {
    code: string;
    message: string;
    data?: any;
    issues?: any[];
  };
};

export type NotificationSettings = {
  emailNotifications: boolean;
  newBids: boolean;
  auctionEnding: boolean;
  outbidNotifications: boolean;
};

export type NotificationType = 'bid_placed' | 'auction_ending' | 'outbid' | 'auction_won' | 'auction_started' | 'payment_required' | 'auction_cancelled' | 'outbid_live_auction';

export type NotificationStatus = 'pending' | 'sent' | 'failed' | 'read';

export type NotificationChannel = 'email' | 'socket' | 'web_push';

export type Notification = {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: any;
  channels: NotificationChannel[];
  status: NotificationStatus;
  readAt?: string;
  createdAt: string;
  sentAt?: string;
  failureReason?: string;
};

export type NotificationLog = {
  id: string;
  notificationId: string;
  userId: string;
  channel: NotificationChannel;
  status: 'sent' | 'failed';
  sentAt: string;
  failureReason?: string;
  metadata?: any;
};

/**
 * What a donation of a given size costs, worked out by the server.
 *
 * Both sides of the choice come back together so the pill toggle can switch
 * without another round trip, and every figure is in cents.
 */
export type DonationFeeQuote = {
  chargeCents: number;
  charityReceivesCents: number;
  platformFeeCents: number;
  processingFeeCents: number;
  donorCoveredCents: number;
};

export type DonationQuote = {
  donationCents: number;
  currency: string;
  platformFeePercentage: number;
  auctioneerName: string | null;
  charityName: string | null;
  charityAbsorbsFees: DonationFeeQuote;
  donorCoversFees: DonationFeeQuote;
};

/** An auctioneer's offer of an item to one person at a set price. Amounts are in cents. */
export type ItemOffer = {
  id: string;
  itemId: string;
  auctionId: string;
  email: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'accepted' | 'declined' | 'cancelled' | 'expired';
  expiresAt: string;
  createdByUserId: string;
  createdByName?: string;
  createdAt: string;
  respondedAt?: string;
  acceptedByUserId?: string;
  bidId?: string;
  transactionId?: string;
  /** What happened to the offer, oldest first. Only on the auctioneer's list. */
  history?: ItemOfferEvent[];
};

/** One step in an offer's life, for the auctioneer's audit trail. */
export type ItemOfferEvent = {
  type: 'sent' | 'resent' | 'withdrawn' | 'replaced' | 'declined' | 'payment_failed' | 'accepted' | 'expired';
  at: string;
  by?: string;
  /** For sends: whether the email went out. */
  emailSent?: boolean;
  detail?: string;
};

/** What the person an offer was sent to sees when they open its link. */
export type OfferForRecipient = {
  offer: ItemOffer;
  item: { id: string; title: string; imageUrl?: string };
  auction: { id: string; title: string; currency: string };
  offeredBy: string;
  paymentMethods: Array<{ id: string; brand?: string; last4?: string; expMonth?: number; expYear?: number; isDefault: boolean }>;
};
